import { useAuth, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { PaginatedList } from "@tellescope/design-system";
import { useMemo } from "react";
import { useCareTeamEnduserIds, useInboxSearchParams } from "../../hooks";
import { useInboxTableViews } from "../../../../hooks";
import { buildInboxMdbFilter, buildInboxViewScopeFilter, getInboxView } from "../../utils";
import { InboxThreadListDefaultItem } from "./list-items";
import { InboxThreadAutoSelect } from "./InboxThreadAutoSelect";

/**
 * Component for rendering the inbox thread list
 * Isolated for better performance - only re-renders when threads change
 */
export const InboxThreadList = () => {
  const location = useLocation();
  const view = getInboxView(location);
  const { currentUser } = useAuth();
  const [inboxQueryParams] = useInboxSearchParams();
  const { effectiveFilters } = useInboxTableViews();

  const mdbFilter = useMemo<object | undefined>(
    () => ({
      // Which threads this route may show at all is derived from the route, never from the saved
      // view — a view saved in Archive must not surface archived threads in the Inbox.
      ...buildInboxViewScopeFilter(view),
      ...buildInboxMdbFilter(effectiveFilters, currentUser.id),
    }),
    [view, effectiveFilters, currentUser.id]
  );
  const { t } = useTranslation("inbox");
  const selectedThreadId = useParams().threadId;

  // Care team can't be expressed in mdbFilter (see useCareTeamEnduserIds), so it arrives as a
  // resolved enduserIds constraint instead.
  const { enduserIds: careTeamEnduserIds, isLoading: isLoadingCareTeam } = useCareTeamEnduserIds(
    effectiveFilters.careTeamIds
  );

  const threadsQuery = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      autobuild: true,
      limit: inboxQueryParams.search ? undefined : 50,
      mdbFilter,
      enduserIds: careTeamEnduserIds,
      search: inboxQueryParams.search || undefined,
    },
    // Waiting avoids one unfiltered fetch before the care team resolves.
    enabled: !isLoadingCareTeam,
    getNextPageParam: (lastPage) => lastPage.threads[lastPage.threads.length - 1]?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({
      ...request,
      lastTimestamp,
    }),
    select: (data) =>
      Array.from(
        data.pages
          .reduce(
            (byId, page) => page.threads.reduce((acc, thread) => acc.set(thread.id, thread), byId),
            new Map<string, (typeof data.pages)[number]["threads"][number]>()
          )
          .values()
      ).sort((a, b) => {
        // The BE sort is unreliable, we need to correct the sort to be the latest of the two timestamps
        const aLatest =
          a.outboundTimestamp && a.outboundTimestamp.getTime() >= a.timestamp.getTime()
            ? a.outboundTimestamp
            : a.timestamp;
        const bLatest =
          b.outboundTimestamp && b.outboundTimestamp.getTime() >= b.timestamp.getTime()
            ? b.outboundTimestamp
            : b.timestamp;

        return bLatest.getTime() - aLatest.getTime();
      }),
  });

  if (threadsQuery.isError) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <div className="text-red-500 mb-2">{t("Error loading inbox threads")}</div>
      </div>
    );
  }

  if (threadsQuery.isLoading) {
    return <div className="p-4 text-center text-muted-foreground">{t("Loading messages...")}</div>;
  }

  if (threadsQuery.data?.length === 0) {
    return <div className="p-4 text-center text-muted-foreground">{t("No messages found")}</div>;
  }

  return (
    <>
      <InboxThreadAutoSelect threads={threadsQuery.data} />
      <PaginatedList
        className="px-4 pb-4 space-y-2 flex-1"
        onBottomReached={() => {
          threadsQuery.fetchNextPage();
        }}
        resetKey={JSON.stringify(mdbFilter)}
      >
        {threadsQuery.data?.map((thread) => (
          <InboxThreadListDefaultItem
            key={thread.id}
            thread={thread}
            isSelected={selectedThreadId === thread.id}
            className="my-1"
          />
        ))}
      </PaginatedList>
    </>
  );
};

InboxThreadList.displayName = "InboxThreadList";
